import { Link, useNavigate } from "@tanstack/react-router";
import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { useT } from "~/i18n";

export const MAIN_CONTENT_ID = "main-content";

/**
 * First focusable element in the document (WCAG 2.2 §2.4.1). It is not hidden
 * with `display:none`; it is translated off-screen and slides in on focus so
 * keyboard users get an unambiguous target.
 */
export function SkipLink({
  to,
  children,
}: {
  to?: "/overview";
  children?: ReactNode;
}) {
  const t = useT();
  const navigate = useNavigate();
  const label = children ?? t("a11y.skipToContent");

  function leaveCaderno(event: KeyboardEvent<HTMLAnchorElement>) {
    if (!to) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    void navigate({ to, search: {} });
  }

  function focusMainContent(event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    document.getElementById(MAIN_CONTENT_ID)?.focus({ preventScroll: false });
  }

  if (to) {
    return (
      <Link className="skip-link" to={to} search={{}} onKeyDown={leaveCaderno}>
        {label}
      </Link>
    );
  }

  return (
    <a className="skip-link" href={`#${MAIN_CONTENT_ID}`} onClick={focusMainContent}>
      {label}
    </a>
  );
}
